import { OrderByDirection } from '../../../../../../../../typings/API.interface';
import { Event } from '../../../../../../../../typings/Event.interface';
import { EventAttempt } from '../../../../../../../../typings/EventAttempt.interface';
import { APIWebhook } from '../../../../../../../../typings/Webhook.interface';
import { numberWithCommas } from '../../../../../utils';
import { getCurrentTimezoneAbreviation } from '../../../../../utils/date';
import Badge from '../../../../common/base/Badge';
import Button from '../../../../common/base/Button';
import LabelButton from '../../../../common/base/LabelButton';
import Text from '../../../../common/base/Text';
import DisplayDate from '../../../../common/DisplayDate';
import { Div } from '../../../../common/helpers/StyledUtils';
import { Status } from '../../../../common/Status';
import { ATTEMPT_TRIGGER_LABELS } from '../../../../../utils/attempt';

interface EventListLayout {
  [k: string]: (dir: OrderByDirection) => {
    widths: number[] | { min?: number; max?: number }[];
    headers: (string | JSX.Element)[];
    getEventRowFields: (
      event: Event,
      outdated: boolean,
      actions: {
        retry: (event: Event) => void;
        mute: (event: Event) => void;
        bookmark: (event: Event) => void;
      },
      webhook?: APIWebhook,
      cli_client?: any,
    ) => (string | JSX.Element | object)[];
    getAttemptRowFields: (
      attempt: EventAttempt,
      outdated: boolean,
      actions: {
        retry: (event: Event) => void;
        mute: (event: Event) => void;
      },
    ) => (string | JSX.Element | object)[];
  };
}

const event_list_layouts: EventListLayout = {
  cli: () => ({
    widths: [
      { max: 172, min: 172 },
      { max: 140, min: 140 },
      { min: 100 },
      { min: 128, max: 128 },
      { max: 172, min: 172 },
    ],
    headers: [
      `Event Date (${getCurrentTimezoneAbreviation()})`,
      'Status',
      'Connection',
      'Attempts',
      'Device Name',
    ],
    getEventRowFields: (event, outdated, actions, webhook, client) => [
      event.created_at && <DisplayDate date={event.created_at} />,
      <Div key={`${event.id}-status`} flex={{ gap: 2, justify: 'space-between', align: 'center' }}>
        <Status small key={event.id} {...event} />
        {event.status !== 'QUEUED' && webhook && (
          <Button.Permission
            role="member"
            small
            minimal
            icon="retry"
            muted
            m={{ y: -1.5 }}
            onClick={(e) => {
              e.stopPropagation();
              actions.retry(event);
            }}
          />
        )}
      </Div>,
      webhook ? (
        <LabelButton
          to={`/connections/${webhook.id}`}
          monospace
          neutral
          label={webhook.full_name}
          small
        />
      ) : (
        <Text muted size="s">
          Connection deleted
        </Text>
      ),
      `${event.attempts || 0}`,
      client?.device_name,
    ],
    getAttemptRowFields: (attempt) => [
      <DisplayDate key={`${attempt.id}-date`} date={attempt.created_at} />,
      <Status small key={`${attempt.id}-status`} {...attempt} />,
      <Badge small key={`${attempt.id}-trigger`} muted>
        {ATTEMPT_TRIGGER_LABELS[attempt.trigger]}
      </Badge>,
      <Text key={`${attempt.id}-delivery`} size="s" monospace>
        {!attempt.delivery_latency ? '-' : `${numberWithCommas(attempt.delivery_latency)}ms`}
      </Text>,
      <Text key={`${attempt.id}-response`} size="s" monospace>
        {!attempt.response_latency ? '-' : `${numberWithCommas(attempt.response_latency)}ms`}
      </Text>,
    ],
  }),
  http: () => ({
    widths: [
      { max: 172, min: 172 },
      { max: 140, min: 140 },
      { min: 100 },
      { min: 128, max: 128 },
      { max: 174, min: 174 },
    ],
    headers: [
      `Event Date (${getCurrentTimezoneAbreviation()})`,
      'Status',
      'Connection',
      'Attempts',
      'Next Attempt',
    ],
    getEventRowFields: (event, outdated, actions, webhook) => [
      event.created_at && <DisplayDate date={event.created_at} />,
      <Div key={`${event.id}-status`} flex={{ gap: 2, justify: 'space-between', align: 'center' }}>
        <Status small key={event.id} {...event} />
        {event.status !== 'QUEUED' && webhook && (
          <Button.Permission
            role="member"
            small
            minimal
            icon="retry"
            muted
            m={{ y: -1.5 }}
            onClick={(e) => {
              e.stopPropagation();
              actions.retry(event);
            }}
          />
        )}
      </Div>,
      webhook ? (
        <LabelButton
          to={`/connections/${webhook.id}`}
          monospace
          neutral
          label={webhook.full_name}
          small
        />
      ) : (
        <Text muted size="s">
          Connection deleted
        </Text>
      ),
      `${event.attempts || 0}`,
      event.next_attempt_at && (
        <Div
          key={`${event.id}-next-attempt`}
          flex={{ gap: 2, justify: 'space-between', align: 'center' }}>
          <DisplayDate date={event.next_attempt_at} />
          <Button.Permission
            role="member"
            small
            minimal
            icon="cancel"
            muted
            m={{ y: -1.5 }}
            onClick={(e) => {
              e.stopPropagation();
              actions.mute(event);
            }}
          />
        </Div>
      ),
    ],
    getAttemptRowFields: (attempt) => [
      <DisplayDate key={`${attempt.id}-date`} date={attempt.created_at} />,
      <Status small key={`${attempt.id}-status`} {...attempt} />,
      <Badge small key={`${attempt.id}-trigger`} muted>
        {ATTEMPT_TRIGGER_LABELS[attempt.trigger]}
      </Badge>,
      <Text key={`${attempt.id}-delivery`} muted size="s" monospace>
        {!attempt.delivery_latency ? '-' : `${numberWithCommas(attempt.delivery_latency)}ms`}
      </Text>,
      <Text key={`${attempt.id}-response`} muted size="s" monospace>
        {!attempt.response_latency ? '-' : `${numberWithCommas(attempt.response_latency)}ms`}
      </Text>,
    ],
  }),
};

export default event_list_layouts;
